// @flow

/**
 * StandardLayoutEngine builds a traditional multi-column "Pinterest grid"
 * layout.
 */

import MultiColumnLayoutEngine from 'app/common/react/components/Masonry9/MultiColumnLayoutEngine';
import { type buildLayoutProps } from 'app/common/react/components/Masonry9/LayoutEngine';
import Layout, {
  type Size,
  type ItemLayoutInfo,
} from 'app/common/react/components/Masonry9/Layout';

export default class StandardLayoutEngine<ItemType> extends MultiColumnLayoutEngine<ItemType> {
  buildLayout({
    containerSize,
    items,
    previousLayout,
  }: buildLayoutProps<ItemType>): Layout<ItemType> {
    // Get column width and count.
    const { width: colWidth, count: colCount } = this.getColumnInfo(containerSize);

    // Initialize layout data.
    const columnHeights = new Array(colCount).fill(0);

    // Create a new layout with no layout info. We'll iterate over each item in
    // the previous layout (if specified) and copy over layout information.
    // $FlowFixMe: new errors found from flow 0.96 upgrade
    const newItemsLayoutInfo: WeakMap<ItemType, ItemLayoutInfo> = new WeakMap();

    // Keep track of whether the layout needs measurements. If any pins need
    // measuring, we'll set this to true.
    let isMeasuring = false;

    // Keep track of the layout's overall size as we build it.
    const layoutSize: Size = {
      width: 0,
      height: 0,
    };

    // Iterate over all items and add an entry in newLayout for each.
    items.forEach((item, index) => {
      const itemLayoutInfo = previousLayout ? previousLayout.getItemLayoutInfo(item) : undefined;

      // If we have layout information for an item, we'll try to lay it out. If
      // not, we'll add it to the list to be measured.
      if (colCount > 0 && itemLayoutInfo && itemLayoutInfo.size) {
        // If we have layout info and the item doesn't need measuring, we must
        // have a width and height on the frame.
        const itemSize = itemLayoutInfo.size;
        if (!itemSize.width || !itemSize.height) {
          throw new Error('Items that have been measured should always have a width and height.');
        }

        // Find the column to slot this item into, the left-most slot in the
        // case of a colspan. Use the original column heights to choose where
        // items go because after resizing, we would otherwise possibly shuffle
        // the grid.
        const newColumn = StandardLayoutEngine.getColumnIndex(columnHeights);
        const columnHeight = columnHeights[newColumn];

        // Pick the position based on existing column heights.
        const gutterHeight = columnHeight === 0 ? 0 : this.gutter;

        // Pick a top based on the lowest grid item in the target columns. We
        // may change this if we level the pins above.
        const newItemTop = columnHeight + gutterHeight;
        const newItemLeft = newColumn * (colWidth + this.gutter);

        // Add this item to the min-height column. Its height and width won't
        // change from this point, but its position will.
        newItemsLayoutInfo.set(item, {
          ...itemLayoutInfo,
          size: {
            ...itemLayoutInfo.size,
            width: colWidth,
          },
          position: {
            left: newItemLeft,
            top: newItemTop,
          },
          customIndex: index,
        });

        // Update column height. In order to update the original height columns,
        // we need to measure where this item would go if not leveled.
        columnHeights[newColumn] = newItemTop + itemSize.height;

        // Update the max layout size.
        if (layoutSize.width < newItemLeft + itemSize.width) {
          layoutSize.width = newItemLeft + itemSize.width;
        }
        if (layoutSize.height < newItemTop + itemSize.height) {
          layoutSize.height = newItemTop + itemSize.height;
        }
      } else {
        // Add the item to the list to have its height measured.
        newItemsLayoutInfo.set(item, {
          constraints: { width: colWidth },
        });

        // We'll assume that this layout will be updated in the future with once
        // pins are measured.
        isMeasuring = true;
      }
    });

    const newLayout = new Layout(items, (item) => newItemsLayoutInfo.get(item));

    newLayout.size = layoutSize;
    newLayout.contentFilledSize = {
      width: layoutSize.width,
      height: Math.min(...columnHeights),
    };
    newLayout.isMeasuring = isMeasuring;

    return newLayout;
  }
}
